import React from 'react';
import { makeStyles } from 'tss-react/mui';
import {
    TemplateColumn,
    TimesheetDay,
    TimesheetTemplate,
} from 'api/report/types';
import { extractHoursAndMinutes, formatHoursToHHMM } from 'utils/utils_legacy';
import Typography from '@mui/material/Typography';
import TextField, { Label } from 'components/interface/textField/TextField';
import {
    extractNumber,
    extractMinutesFromHoursDecimals,
    minutesToHoursDecimals,
} from 'utils/utils';
import { red } from '@mui/material/colors';
import { Box, lighten } from '@mui/material';
import Symbol from 'components/Symbol';

const useStyles = makeStyles()(theme => ({
    base: {
        width: '100%',
    },
    group: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(2),
    },
    groupTitle: {
        marginBottom: theme.spacing(1),
    },

    textFieldRowWrapper: {
        'width': '100%',
        'display': 'flex',
        'flexDirection': 'column',
        '& + &': {
            marginTop: theme.spacing(1),
        },
    },
    textFieldWrapper: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        columnGap: theme.spacing(1),
    },
    textField: {
        width: '100%',
    },
    splitTextField: {
        flexBasis: '48%',
    },
    isSickHours: {},
    sickHoursIcon: {
        color: red[900],
    },
}));

interface Props {
    day: TimesheetDay | undefined;
    dayIndex: number;
    template: TimesheetTemplate;
    useSplitTextfields: boolean;
    canSubmit: boolean;
    disabled: boolean;
    loading: boolean;
    enableSickHours: boolean;
    onUpdateTimesheetDay: (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => void;
}

export default function NormalTimesheet({
    day,
    dayIndex,
    template,
    useSplitTextfields,
    canSubmit,
    disabled,
    loading,
    enableSickHours,
    onUpdateTimesheetDay,
}: Props): JSX.Element {
    const { classes } = useStyles();
    const [errors, setErrors] = React.useState<{ [key: string]: boolean }>({});
    const subheaders = template.subheader ?? [];
    const headers = template.header ?? [];

    const handleUpdateTimesheetDay = (
        value: string,
        event: React.ChangeEvent<HTMLInputElement>,
        columnIndex: number,
        type: 'hour' | 'minutes',
        isSickHours: boolean
    ) => {
        if (event?.target?.validity?.badInput) {
            setErrors(prev => ({
                ...prev,
                [columnIndex + '-' + isSickHours]: true,
            }));
        } else if (errors[columnIndex + '-' + isSickHours]) {
            setErrors(prev => ({
                ...prev,
                [columnIndex + '-' + isSickHours]: false,
            }));
        }

        let maxHours = 24;
        if (subheaders[columnIndex].maxHours) {
            maxHours = subheaders[columnIndex].maxHours;
        }

        const minFloat = (a: number, b: number) =>
            a > b ? extractNumber(b + '', 3) : extractNumber(a + '', 3);

        const key = isSickHours ? 'sickHours' : 'hours';

        onUpdateTimesheetDay(prev => {
            const d = { ...prev };
            d[key] = [...d[key]];

            if (useSplitTextfields) {
                // when using split textfields you enter hours and minutes separate. Therefore the logic is special.
                if (type === 'hour') {
                    value = extractNumber(value, 3);
                    value = parseFloat(value) > 24 ? '24' : value + '';
                    const hour = value ? parseInt(value) : 0;
                    const minutesDecimals = extractMinutesFromHoursDecimals(
                        d[key][columnIndex]
                    ); // This turns for example 4.25 into 0.25, it only keeps the decimals
                    d[key][columnIndex] = minFloat(
                        hour + minutesDecimals,
                        maxHours
                    );
                } else {
                    value = extractNumber(value, 3);
                    value = parseFloat(value) > 59 ? '59' : value + '';
                    const hour = d[key][columnIndex]
                        ? parseInt(d[key][columnIndex])
                        : 0;
                    const minutesDecimals = minutesToHoursDecimals(value); // This turns for example 15 into 0.25
                    d[key][columnIndex] = minFloat(
                        hour + minutesDecimals,
                        maxHours
                    );
                }
            } else {
                value = extractNumber(value, 3);
                d[key][columnIndex] =
                    parseFloat(value) > maxHours ? maxHours + '' : value + '';
            }

            return d;
        }, dayIndex);
    };

    const buildGroups = (): Array<JSX.Element> => {
        const returnData: Array<JSX.Element> = [];
        if (!day) {
            return returnData;
        }
        let start = 0;
        for (let i = 0; i < headers.length; i++) {
            let colspan = parseInt(headers[i].colspan);
            // This is because the first header stretches over the column with the dates, meaning that its colspan is one too large.
            if (i === 0) {
                colspan--;
            }
            const end = start + colspan;
            returnData.push(
                buildGroup(
                    headers[i].text,
                    subheaders.slice(start, end),
                    day.hours.slice(start, end),
                    day.sickHours.slice(start, end),
                    start
                )
            );
            start += colspan;
        }
        return returnData;
    };

    const buildGroup = (
        name: string,
        subheader: Array<TemplateColumn>,
        hours: Array<string>,
        sickHours: Array<string>,
        columnStartIndex: number
    ): JSX.Element => {
        const returnData = [];

        for (let i = 0; i < subheader.length; i++) {
            returnData.push(
                buildTextField(
                    subheader[i].text,
                    hours[i],
                    columnStartIndex + i,
                    false
                )
            );
            if (enableSickHours) {
                returnData.push(
                    buildTextField(
                        subheader[i].text,
                        sickHours[i],
                        columnStartIndex + i,
                        true
                    )
                );
            }
        }
        return (
            <div key={name} className={classes.group}>
                <Typography
                    variant='body1'
                    color='textPrimary'
                    className={classes.groupTitle}>
                    <b>{name}</b>
                </Typography>
                {returnData}
            </div>
        );
    };

    const buildTextField = (
        label: string,
        value: string,
        columnIndex: number,
        isSickHours: boolean
    ): JSX.Element => {
        const splitTime = useSplitTextfields
            ? extractHoursAndMinutes(value)
            : null;
        const hourText = useSplitTextfields
            ? splitTime?.hours?.toString()
            : value;
        const minutesText = useSplitTextfields
            ? splitTime?.minutes?.toString()
            : '';
        return (
            <div
                className={classes.textFieldRowWrapper}
                key={columnIndex + 'wrapper' + isSickHours}>
                {isSickHours ? null : (
                    <Label text={label.replaceAll('<br>', ' ')} />
                )}
                <div className={classes.textFieldWrapper}>
                    <TextField
                        key={columnIndex}
                        value={hourText || ''}
                        type='number'
                        lang='en'
                        inputMode='decimal'
                        onChange={(v, e) =>
                            handleUpdateTimesheetDay(
                                v,
                                e,
                                columnIndex,
                                'hour',
                                isSickHours
                            )
                        }
                        disabled={!canSubmit || disabled || loading}
                        endAdornment={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    paddingLeft: 1,
                                }}>
                                <Typography variant='body2' color='inherit'>
                                    timmar
                                </Typography>
                                {isSickHours ? (
                                    <Symbol
                                        symbol='sick'
                                        color='red-dark'
                                        size='lg'
                                        tooltip='Sjuktimmar'
                                    />
                                ) : null}
                            </Box>
                        }
                        variantSx={{
                            backgroundColor: isSickHours
                                ? lighten(red[50], 0.4)
                                : undefined,
                        }}
                        error={errors[columnIndex + '-' + isSickHours]}
                        helperText={
                            errors[columnIndex + '-' + isSickHours]
                                ? 'Felaktigt format.'
                                : useSplitTextfields
                                  ? ''
                                  : formatHoursToHHMM(hourText, true)
                        }
                        fullWidth={!useSplitTextfields}
                    />
                    {useSplitTextfields ? (
                        <TextField
                            key={columnIndex + 'minutes'}
                            value={minutesText || ''}
                            type='number'
                            onChange={(v, e) =>
                                handleUpdateTimesheetDay(
                                    v,
                                    e,
                                    columnIndex,
                                    'minutes',
                                    isSickHours
                                )
                            }
                            disabled={!canSubmit || disabled || loading}
                            endAdornment={
                                <Typography variant='body2' color='inherit'>
                                    minuter
                                </Typography>
                            }
                            error={errors[columnIndex + '-' + isSickHours]}
                            helperText={
                                errors[columnIndex + '-' + isSickHours]
                                    ? ' '
                                    : ''
                            }
                        />
                    ) : null}
                </div>
            </div>
        );
    };

    return <div className={classes.base}>{buildGroups()}</div>;
}
